import { fetchAuthSession } from "@aws-amplify/auth";

// ── SigV4 primitives ──────────────────────────────────────────────────────────

const buf2hex = (buffer) =>
  Array.from(new Uint8Array(buffer))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");

const sha256Hex = async (message) => {
  const buf = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(message),
  );
  return buf2hex(buf);
};

const hmac = (key, message) =>
  crypto.subtle
    .importKey(
      "raw",
      typeof key === "string" ? new TextEncoder().encode(key) : key,
      { name: "HMAC", hash: "SHA-256" },
      false,
      ["sign"],
    )
    .then((k) =>
      crypto.subtle.sign("HMAC", k, new TextEncoder().encode(message)),
    );

const hmacHex = async (key, message) => buf2hex(await hmac(key, message));

const getSigningKey = async (secretKey, dateStamp, region, service) => {
  const kDate = await hmac("AWS4" + secretKey, dateStamp);
  const kRegion = await hmac(kDate, region);
  const kService = await hmac(kRegion, service);
  return hmac(kService, "aws4_request");
};

// ── Main helper ───────────────────────────────────────────────────────────────

/**
 * Signs a POST request with AWS SigV4 and sends it.
 * Passes the Cognito access token in the body as `token` for Lambda auth.
 *
 * @param {string} url        - Full API Gateway endpoint URL
 * @param {object} body       - Request payload (will be JSON-stringified)
 * @param {string} [region]   - AWS region (default: us-east-2)
 * @returns {{ lambdaStatus: number, data: object }}
 */
export const signedPost = async (url, body, region = "us-east-2") => {
  const session = await fetchAuthSession();
  const accessToken = session.tokens?.accessToken?.toString();
  const credentials = session.credentials;

  if (!accessToken) throw new Error("Missing Cognito access token.");
  if (!credentials?.accessKeyId || !credentials?.secretAccessKey)
    throw new Error("Missing IAM credentials for SigV4 signing.");

  const service = "execute-api";
  const method = "POST";
  const bodyPayload = JSON.stringify({ ...body, token: accessToken });

  const now = new Date();
  const amzDate = now.toISOString().slice(0, 19).replace(/[-:]/g, "") + "Z";
  const dateStamp = amzDate.slice(0, 8);

  const urlObj = new URL(url);
  const host = urlObj.host;
  const canonicalUri = urlObj.pathname;
  const bodyHash = await sha256Hex(bodyPayload);
  const hasToken = Boolean(credentials.sessionToken);

  const canonicalHeaders =
    `content-type:application/json\nhost:${host}\nx-amz-date:${amzDate}\n` +
    (hasToken ? `x-amz-security-token:${credentials.sessionToken}\n` : "");
  const signedHeaders =
    "content-type;host;x-amz-date" +
    (hasToken ? ";x-amz-security-token" : "");

  const canonicalRequest = [
    method,
    canonicalUri,
    "",
    canonicalHeaders,
    signedHeaders,
    bodyHash,
  ].join("\n");

  const credentialScope = `${dateStamp}/${region}/${service}/aws4_request`;
  const stringToSign = [
    "AWS4-HMAC-SHA256",
    amzDate,
    credentialScope,
    await sha256Hex(canonicalRequest),
  ].join("\n");

  const signingKey = await getSigningKey(
    credentials.secretAccessKey,
    dateStamp,
    region,
    service,
  );
  const signature = await hmacHex(signingKey, stringToSign);
  const authHeader = `AWS4-HMAC-SHA256 Credential=${credentials.accessKeyId}/${credentialScope}, SignedHeaders=${signedHeaders}, Signature=${signature}`;

  const headers = {
    "Content-Type": "application/json",
    "X-Amz-Date": amzDate,
    Authorization: authHeader,
  };
  if (hasToken) headers["X-Amz-Security-Token"] = credentials.sessionToken;

  const response = await fetch(url, { method, headers, body: bodyPayload });
  const rawData = await response.json();

  // Non-proxy integration: Lambda response is nested {statusCode, headers, body}
  const isNonProxy =
    rawData &&
    typeof rawData.statusCode === "number" &&
    typeof rawData.body === "string";
  const lambdaStatus = isNonProxy ? rawData.statusCode : response.status;
  const data = isNonProxy ? JSON.parse(rawData.body) : rawData;

  return { lambdaStatus, data };
};
